/**
 * /settings/compliance — licensing records (migration 0100).
 *
 * Per facility: the operating licence(s) held (authority, number,
 * activities, star rating, issue / expiry / inspection dates), an expiry
 * badge (60-day reminder window mirrors `pollLicenceExpiry`), and the
 * inspection-pack builder (facility + date range → TAR download link).
 *
 * Capability: compliance.records. Jurisdiction-agnostic — the copy names the
 * UK regulation as the motivating example only.
 */
import {
  type FacilityLicence,
  type FacilityLicenceInput,
  type InspectionPackResult,
  createFacilityLicence,
  createInspectionPack,
  deleteFacilityLicence,
  listAllLicences,
  listFacilities,
  updateFacilityLicence,
} from '@/lib/api';
import type { Facility } from '@/lib/types';
import { AlertTriangle, Download, Plus, Star, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

const ACTIVITY_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'boarding_dogs', label: 'Boarding (dogs)' },
  { value: 'boarding_cats', label: 'Boarding (cats)' },
  { value: 'daycare', label: 'Day care' },
  { value: 'home_boarding', label: 'Home boarding' },
  { value: 'grooming', label: 'Grooming' },
];

export const LICENCE_REMINDER_WINDOW_DAYS = 60;

export function daysUntil(ymd: string | null): number | null {
  if (!ymd) return null;
  const target = new Date(`${ymd}T00:00:00Z`).getTime();
  if (Number.isNaN(target)) return null;
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((target - todayUtc) / 86_400_000);
}

function ymdToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function ymdOffset(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);
}

interface LicenceFormState {
  authorityName: string;
  licenceNumber: string;
  activityTypes: string[];
  starRating: string;
  issuedOn: string;
  expiresOn: string;
  lastInspectionOn: string;
  nextInspectionDueOn: string;
  notes: string;
}

const EMPTY_FORM: LicenceFormState = {
  authorityName: '',
  licenceNumber: '',
  activityTypes: [],
  starRating: '',
  issuedOn: '',
  expiresOn: '',
  lastInspectionOn: '',
  nextInspectionDueOn: '',
  notes: '',
};

function fromLicence(l: FacilityLicence): LicenceFormState {
  return {
    authorityName: l.authorityName,
    licenceNumber: l.licenceNumber ?? '',
    activityTypes: l.activityTypes,
    starRating: l.starRating != null ? String(l.starRating) : '',
    issuedOn: l.issuedOn ?? '',
    expiresOn: l.expiresOn ?? '',
    lastInspectionOn: l.lastInspectionOn ?? '',
    nextInspectionDueOn: l.nextInspectionDueOn ?? '',
    notes: l.notes ?? '',
  };
}

function toInput(f: LicenceFormState): FacilityLicenceInput {
  const nul = (s: string) => (s.trim().length > 0 ? s.trim() : null);
  return {
    authorityName: f.authorityName.trim(),
    licenceNumber: nul(f.licenceNumber),
    activityTypes: f.activityTypes,
    starRating: f.starRating ? Number.parseInt(f.starRating, 10) : null,
    issuedOn: nul(f.issuedOn),
    expiresOn: nul(f.expiresOn),
    lastInspectionOn: nul(f.lastInspectionOn),
    nextInspectionDueOn: nul(f.nextInspectionDueOn),
    notes: nul(f.notes),
  };
}

const inputCls = 'w-full rounded-md border border-border bg-background px-3 py-2 text-sm';
const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground';

function ExpiryBadge({ expiresOn }: { expiresOn: string | null }) {
  const days = daysUntil(expiresOn);
  if (days === null) {
    return <span className="text-xs text-muted-foreground">No expiry recorded</span>;
  }
  if (days < 0) {
    return (
      <span className="num flex items-center gap-1 rounded-full bg-destructive/10 px-2.5 py-0.5 text-[11px] font-medium text-destructive ring-1 ring-inset ring-destructive/25">
        <AlertTriangle className="h-3 w-3" aria-hidden />
        Expired {Math.abs(days)} days ago
      </span>
    );
  }
  if (days <= LICENCE_REMINDER_WINDOW_DAYS) {
    return (
      <span className="num flex items-center gap-1 rounded-full bg-warning/15 px-2.5 py-0.5 text-[11px] font-medium text-warning-foreground ring-1 ring-inset ring-warning/30">
        <AlertTriangle className="h-3 w-3" aria-hidden />
        Expires in {days} days
      </span>
    );
  }
  return (
    <span className="num rounded-full bg-success/10 px-2.5 py-0.5 text-[11px] font-medium text-success ring-1 ring-inset ring-success/25">
      Expires in {days} days
    </span>
  );
}

function StarRating({ value }: { value: number | null }) {
  if (value === null) return <span className="text-xs text-muted-foreground">Not rated</span>;
  return (
    <span className="num flex items-center gap-0.5" aria-label={`${value} star rating`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${n <= value ? 'fill-warning text-warning' : 'text-border'}`}
          strokeWidth={1.5}
          aria-hidden
        />
      ))}
      <span className="ml-1 text-xs text-muted-foreground">{value}/5</span>
    </span>
  );
}

interface LicenceFormProps {
  initial: LicenceFormState;
  submitLabel: string;
  onSubmit: (input: FacilityLicenceInput) => Promise<void>;
  onCancel: () => void;
}

function LicenceForm({ initial, submitLabel, onSubmit, onCancel }: LicenceFormProps) {
  const [form, setForm] = useState<LicenceFormState>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof LicenceFormState>(k: K, v: LicenceFormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggleActivity = (value: string) =>
    set(
      'activityTypes',
      form.activityTypes.includes(value)
        ? form.activityTypes.filter((a) => a !== value)
        : [...form.activityTypes, value],
    );

  const submit = async () => {
    if (form.authorityName.trim().length === 0) {
      setError('Licensing authority is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(toInput(form));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the licence.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="lic-authority" className={labelCls}>
            Licensing authority
          </label>
          <input
            id="lic-authority"
            type="text"
            value={form.authorityName}
            onChange={(e) => set('authorityName', e.target.value)}
            placeholder="e.g. Glasgow City Council"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="lic-number" className={labelCls}>
            Licence number
          </label>
          <input
            id="lic-number"
            type="text"
            value={form.licenceNumber}
            onChange={(e) => set('licenceNumber', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="lic-star" className={labelCls}>
            Star rating
          </label>
          <select
            id="lic-star"
            value={form.starRating}
            onChange={(e) => set('starRating', e.target.value)}
            className={inputCls}
          >
            <option value="">Not rated</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={String(n)}>
                {n} star{n > 1 ? 's' : ''}
              </option>
            ))}
          </select>
        </div>
        <fieldset className="sm:col-span-2">
          <legend className={labelCls}>Licensed activities</legend>
          <div className="flex flex-wrap gap-3">
            {ACTIVITY_TYPE_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={form.activityTypes.includes(opt.value)}
                  onChange={() => toggleActivity(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </fieldset>
        <div>
          <label htmlFor="lic-issued" className={labelCls}>
            Issued on
          </label>
          <input
            id="lic-issued"
            type="date"
            value={form.issuedOn}
            onChange={(e) => set('issuedOn', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="lic-expires" className={labelCls}>
            Expires on
          </label>
          <input
            id="lic-expires"
            type="date"
            value={form.expiresOn}
            onChange={(e) => set('expiresOn', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="lic-last-inspection" className={labelCls}>
            Last inspection
          </label>
          <input
            id="lic-last-inspection"
            type="date"
            value={form.lastInspectionOn}
            onChange={(e) => set('lastInspectionOn', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="lic-next-inspection" className={labelCls}>
            Next inspection due
          </label>
          <input
            id="lic-next-inspection"
            type="date"
            value={form.nextInspectionDueOn}
            onChange={(e) => set('nextInspectionDueOn', e.target.value)}
            className={inputCls}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="lic-notes" className={labelCls}>
            Notes
          </label>
          <textarea
            id="lic-notes"
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={2}
            className={inputCls}
          />
        </div>
      </div>
      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="press rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={saving}
          className="press rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}

interface FacilityCardProps {
  facility: Facility;
  licences: FacilityLicence[];
  onChanged: () => Promise<void>;
}

function FacilityCard({ facility, licences, onChanged }: FacilityCardProps) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remove = async (licence: FacilityLicence) => {
    if (!window.confirm(`Delete the ${licence.authorityName} licence record?`)) return;
    setError(null);
    try {
      await deleteFacilityLicence(facility.id, licence.id);
      await onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the licence.');
    }
  };

  return (
    <section
      className="rounded-xl border border-border bg-surface"
      aria-label={`${facility.name} licences`}
    >
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
        <h2 className="text-sm font-semibold">{facility.name}</h2>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="press flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
            Add licence
          </button>
        )}
      </div>
      <div className="space-y-3 px-5 py-4">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {licences.length === 0 && !adding && (
          <p className="text-sm text-muted-foreground">
            No licence recorded for this facility yet.
          </p>
        )}
        {licences.map((licence) =>
          editingId === licence.id ? (
            <LicenceForm
              key={licence.id}
              initial={fromLicence(licence)}
              submitLabel="Save changes"
              onCancel={() => setEditingId(null)}
              onSubmit={async (input) => {
                await updateFacilityLicence(facility.id, licence.id, input);
                setEditingId(null);
                await onChanged();
              }}
            />
          ) : (
            <div
              key={licence.id}
              className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border bg-background px-4 py-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {licence.authorityName}
                  {licence.licenceNumber && (
                    <span className="num ml-2 text-xs text-muted-foreground">
                      {licence.licenceNumber}
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {licence.activityTypes.length > 0
                    ? licence.activityTypes
                        .map((a) => ACTIVITY_TYPE_OPTIONS.find((o) => o.value === a)?.label ?? a)
                        .join(', ')
                    : 'No activities listed'}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <StarRating value={licence.starRating} />
                  <ExpiryBadge expiresOn={licence.expiresOn} />
                  {licence.nextInspectionDueOn && (
                    <span className="num text-xs text-muted-foreground">
                      Next inspection {licence.nextInspectionDueOn}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEditingId(licence.id)}
                  className="press text-xs text-muted-foreground hover:text-foreground"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => void remove(licence)}
                  className="press flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                  aria-label={`Delete ${licence.authorityName} licence`}
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                </button>
              </div>
            </div>
          ),
        )}
        {adding && (
          <LicenceForm
            initial={EMPTY_FORM}
            submitLabel="Add licence"
            onCancel={() => setAdding(false)}
            onSubmit={async (input) => {
              await createFacilityLicence(facility.id, input);
              setAdding(false);
              await onChanged();
            }}
          />
        )}
      </div>
    </section>
  );
}

function InspectionPackPanel({ facilities }: { facilities: Facility[] }) {
  const [facilityId, setFacilityId] = useState(facilities[0]?.id ?? '');
  const [from, setFrom] = useState(ymdOffset(-365));
  const [to, setTo] = useState(ymdToday());
  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState<InspectionPackResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!facilityId && facilities[0]) setFacilityId(facilities[0].id);
  }, [facilities, facilityId]);

  const build = async () => {
    if (!facilityId) return;
    setBuilding(true);
    setError(null);
    setResult(null);
    try {
      setResult(await createInspectionPack({ facilityId, from, to }));
    } catch (err) {
      const code = (err as { code?: string }).code;
      setError(
        code === 'files_not_configured'
          ? 'File storage is not configured for this workspace yet — ask your administrator to set up R2 before building inspection packs.'
          : code === 'pack_too_large'
            ? 'Too many stays in this window. Narrow the date range and try again.'
            : err instanceof Error
              ? err.message
              : 'Could not build the inspection pack.',
      );
    } finally {
      setBuilding(false);
    }
  };

  return (
    <section className="rounded-xl border border-border bg-surface" aria-label="Inspection pack">
      <div className="border-b border-border px-5 py-3">
        <h2 className="text-sm font-semibold">Inspection pack</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          One archive with the per-animal register, medication, feeding, daily monitoring, cleaning
          and incident records for a facility and date range, plus the licence details and a README
          mapping each file to the record-keeping conditions. A download link is also emailed to
          you.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-4">
        <div>
          <label htmlFor="pack-facility" className={labelCls}>
            Facility
          </label>
          <select
            id="pack-facility"
            value={facilityId}
            onChange={(e) => setFacilityId(e.target.value)}
            className={inputCls}
          >
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="pack-from" className={labelCls}>
            From
          </label>
          <input
            id="pack-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="pack-to" className={labelCls}>
            To
          </label>
          <input
            id="pack-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className={inputCls}
          />
        </div>
        <div className="flex items-end">
          <button
            type="button"
            onClick={() => void build()}
            disabled={building || !facilityId}
            className="press flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            <Download className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            {building ? 'Building…' : 'Build inspection pack'}
          </button>
        </div>
      </div>
      {error && <p className="px-5 pb-4 text-sm text-destructive">{error}</p>}
      {result && (
        <div
          className="border-t border-border px-5 py-4 text-sm"
          data-testid="inspection-pack-result"
        >
          <p>
            <a
              href={result.downloadUrl}
              className="font-medium text-primary hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              Download {result.filename}
            </a>
            <span className="num ml-2 text-xs text-muted-foreground">
              link valid until {new Date(result.expiresAt).toLocaleString('en-GB')}
            </span>
          </p>
          <p className="num mt-1 text-xs text-muted-foreground">
            {result.counts.animals} animals · {result.counts.stays} stays ·{' '}
            {result.counts.medicationEntries} medication entries · {result.counts.monitoringEntries}{' '}
            daily checks · {result.counts.cleaningEntries} cleans · {result.counts.incidents}{' '}
            incidents · {result.counts.vaccinations} vaccinations
          </p>
        </div>
      )}
    </section>
  );
}

export function ComplianceSettings() {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [licences, setLicences] = useState<FacilityLicence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [facRes, licRes] = await Promise.all([listFacilities(), listAllLicences()]);
      setFacilities(facRes.facilities.filter((f) => f.isActive === 1));
      setLicences(licRes.items);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const licencesByFacility = useMemo(() => {
    const map = new Map<string, FacilityLicence[]>();
    for (const l of licences) {
      const arr = map.get(l.facilityId) ?? [];
      arr.push(l);
      map.set(l.facilityId, arr);
    }
    return map;
  }, [licences]);

  const expiringSoon = licences.filter((l) => {
    const d = daysUntil(l.expiresOn);
    return d !== null && d <= LICENCE_REMINDER_WINDOW_DAYS;
  });

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading licensing records…</p>;
  }
  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Compliance</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Keep each facility's operating licence on record and build an inspection pack from the
          register, medication, feeding, daily-monitoring and cleaning records the platform already
          holds. Modelled on the UK Animal Welfare (Licensing) Regulations 2018 Schedule 4 record
          set; if your jurisdiction doesn't licence boarding, you can leave this page empty.
        </p>
      </div>

      {expiringSoon.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-foreground" aria-hidden />
          <p className="num">
            {expiringSoon.length} licence{expiringSoon.length === 1 ? '' : 's'} expire within{' '}
            {LICENCE_REMINDER_WINDOW_DAYS} days. Owners and managers are emailed at 60, 30, 14, 7
            and 1 days before expiry.
          </p>
        </div>
      )}

      {facilities.length === 0 ? (
        <p className="text-sm text-muted-foreground">Add a facility first to record its licence.</p>
      ) : (
        facilities.map((facility) => (
          <FacilityCard
            key={facility.id}
            facility={facility}
            licences={licencesByFacility.get(facility.id) ?? []}
            onChanged={load}
          />
        ))
      )}

      {facilities.length > 0 && <InspectionPackPanel facilities={facilities} />}
    </div>
  );
}
